const ADMIN_AVATAR_URL =
  'https://gw.alipayobjects.com/zos/antfincdn/XAosXuNZyF/BiazfanxmamNRoxxVxka.png';

export const BOP_ADMIN_TOKEN_KEY = 'bop_admin_token';
export const BOP_ADMIN_USER_INFO_KEY = 'bop_admin_userInfo';

type StoredAdminUserInfo = {
  id?: string | number;
  name?: string;
  username?: string;
  nickname?: string;
  rights?: number;
  regDate?: string;
  iat?: number;
  exp?: number;
  session_exp?: number;
};

const ADMIN_TOKEN_RENEW_WINDOW_SECONDS = 30 * 60;
let authGeneration = 0;
let renewalPromise: Promise<string | null> | undefined;

const getStorage = (): Storage | undefined => {
  if (typeof globalThis === 'undefined') {
    return undefined;
  }

  try {
    return globalThis.localStorage;
  } catch (error) {
    return undefined;
  }
};

const parseJson = <T>(value: string | null): T | undefined => {
  if (!value) {
    return undefined;
  }

  try {
    return JSON.parse(value) as T;
  } catch (error) {
    return undefined;
  }
};

const decodeBase64Url = (value: string): string | undefined => {
  if (typeof globalThis.atob !== 'function') {
    return undefined;
  }

  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');

  try {
    const binary = globalThis.atob(padded);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));

    if (typeof TextDecoder !== 'undefined') {
      return new TextDecoder().decode(bytes);
    }

    return binary;
  } catch (error) {
    return undefined;
  }
};

const decodeJwtPayload = (token: string): StoredAdminUserInfo | undefined => {
  const [, payload] = token.split('.');

  if (!payload) {
    return undefined;
  }

  const decoded = decodeBase64Url(payload);
  return parseJson<StoredAdminUserInfo>(decoded ?? null);
};

export const shouldRenewAdminToken = (token: string, nowMs = Date.now()): boolean => {
  const payload = decodeJwtPayload(token);
  const now = Math.floor(nowMs / 1000);
  const expiresAt = Number(payload?.exp);
  const sessionExpiresAt = Number(payload?.session_exp);
  const isLegacyAdminToken =
    !Number.isFinite(sessionExpiresAt) &&
    Number(payload?.rights) === 9 &&
    Number.isFinite(Number(payload?.iat));

  if (!Number.isFinite(expiresAt) || expiresAt <= now) {
    return false;
  }

  if (!Number.isFinite(sessionExpiresAt) && !isLegacyAdminToken) {
    return false;
  }

  if (expiresAt - now > ADMIN_TOKEN_RENEW_WINDOW_SECONDS) {
    return false;
  }

  if (
    Number.isFinite(sessionExpiresAt) &&
    (sessionExpiresAt <= now || expiresAt >= sessionExpiresAt - 1)
  ) {
    return false;
  }

  return true;
};

export const buildAdminCurrentUser = (userInfo?: Partial<StoredAdminUserInfo>): API.CurrentUser => {
  const displayName = userInfo?.nickname || userInfo?.name || userInfo?.username || 'Admin';

  return {
    name: displayName,
    userid: userInfo?.id !== undefined && userInfo.id !== null ? String(userInfo.id) : undefined,
    avatar: ADMIN_AVATAR_URL,
    access: 'admin',
  };
};

export const getStoredAdminToken = (): string | null => {
  return getStorage()?.getItem(BOP_ADMIN_TOKEN_KEY) ?? null;
};

export const hasStoredAdminToken = (): boolean => {
  return Boolean(getStoredAdminToken());
};

export const getStoredAdminCurrentUser = (): API.CurrentUser | undefined => {
  const storage = getStorage();
  const token = storage?.getItem(BOP_ADMIN_TOKEN_KEY);

  if (!token) {
    return undefined;
  }

  const storedUserInfo = parseJson<StoredAdminUserInfo>(
    storage?.getItem(BOP_ADMIN_USER_INFO_KEY) ?? null,
  );
  const decodedTokenInfo = decodeJwtPayload(token);

  return buildAdminCurrentUser({
    ...decodedTokenInfo,
    ...storedUserInfo,
  });
};

export const persistAdminAuth = (token: string, userInfo?: StoredAdminUserInfo) => {
  const storage = getStorage();

  if (!storage) {
    return;
  }

  authGeneration += 1;
  storage.setItem(BOP_ADMIN_TOKEN_KEY, token);

  if (userInfo) {
    storage.setItem(BOP_ADMIN_USER_INFO_KEY, JSON.stringify(userInfo));
    return;
  }

  storage.removeItem(BOP_ADMIN_USER_INFO_KEY);
};

export const clearAdminAuth = () => {
  const storage = getStorage();

  if (!storage) {
    return;
  }

  authGeneration += 1;
  storage.removeItem(BOP_ADMIN_TOKEN_KEY);
  storage.removeItem(BOP_ADMIN_USER_INFO_KEY);
};

export const renewStoredAdminToken = async (): Promise<string | null> => {
  const storage = getStorage();
  const currentToken = storage?.getItem(BOP_ADMIN_TOKEN_KEY);

  if (!storage || !currentToken || typeof globalThis.fetch !== 'function') {
    return null;
  }

  if (!renewalPromise) {
    const renewalGeneration = authGeneration;
    const tokenBeingRenewed = currentToken;

    renewalPromise = (async () => {
      try {
        const response = await globalThis.fetch('/api/vl/admin/auth/renew', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Authorization: `Bearer ${tokenBeingRenewed}`,
            'X-BO-Client': '1',
          },
        });
        const result = await response.json();
        const renewedToken = typeof result?.token === 'string' ? result.token : null;
        const renewedPayload = renewedToken ? decodeJwtPayload(renewedToken) : undefined;
        const now = Math.floor(Date.now() / 1000);

        if (
          !response.ok ||
          !renewedToken ||
          !renewedPayload?.exp ||
          renewedPayload.exp <= now ||
          authGeneration !== renewalGeneration ||
          storage.getItem(BOP_ADMIN_TOKEN_KEY) !== tokenBeingRenewed
        ) {
          return null;
        }

        storage.setItem(BOP_ADMIN_TOKEN_KEY, renewedToken);
        return renewedToken;
      } catch (error) {
        return null;
      }
    })().finally(() => {
      renewalPromise = undefined;
    });
  }

  return renewalPromise;
};

export const getAdminTokenForRequest = async (requestUrl?: string): Promise<string | null> => {
  const token = getStoredAdminToken();

  if (
    !token ||
    requestUrl?.includes('/api/vl/login') ||
    requestUrl?.includes('/admin/auth/renew')
  ) {
    return token;
  }

  if (shouldRenewAdminToken(token)) {
    const renewedToken = await renewStoredAdminToken();
    return renewedToken ?? getStoredAdminToken();
  }

  return token;
};
